import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { User, ShoppingCart, Mail, Phone, MapPin, Save, Trash2, Plus, Minus, ArrowRight, Package, Clock, CheckCircle2, ShoppingBag } from "lucide-react";
import ProductManager from "../components/admin/ProductManager";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../mock";
import PaintEdges from "../components/PaintEdges";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_PROFILE = { name: "", email: "", phone: "", street: "", zip: "", city: "" };

const Profile = () => {
  const { user, loading } = useAuth();
  const { items, setQty, remove, total, setOpen } = useCart();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/orders`, { withCredentials: true })
      .then((res) => setOrders(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [user]);

  const [profile, setProfile] = useState(EMPTY_PROFILE);

  useEffect(() => {
    if (!user) return;
    localStorage.removeItem("timerstore_profile");
    axios
      .get(`${API}/profile`, { withCredentials: true })
      .then((res) => setProfile({ ...EMPTY_PROFILE, ...res.data }))
      .catch(() => setProfile({ ...EMPTY_PROFILE, name: user.name || "", email: user.email || "" }));
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-sky-200 border-t-[#1b9ad6] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const save = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/profile`, profile, { withCredentials: true });
      toast.success("Deine Daten wurden gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    }
  };

  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  return (
    <main className="relative overflow-hidden bg-gradient-to-b from-[#dff2fc] via-[#ecf9ee] to-[#fff6ea] min-h-[calc(100vh-76px)]">
      <PaintEdges from="#1b9ad6" via="#7cc24a" to="#f7941e" opacity={0.6} />

      <div className="relative mx-auto max-w-[1000px] px-4 sm:px-8 py-14 lg:py-16">
        <div className="flex items-center gap-4 mb-8">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#1273a8] to-[#1b9ad6] flex items-center justify-center shadow-lg text-white font-display text-2xl font-extrabold uppercase">
            {user.name?.[0] || "U"}
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold">
              <span className="text-[#1b7fc1]">Mein</span> <span className="text-[#6fbf3f]">Profil</span>
            </h1>
            <p className="text-slate-500">{user.email}</p>
          </div>
        </div>

        <Tabs defaultValue={user.is_admin ? "produktverwaltung" : "daten"}>
          <TabsList className="bg-white/70 rounded-full p-1.5 h-auto shadow-sm flex-wrap justify-start">
            <TabsTrigger
              value="daten"
              className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1b9ad6] data-[state=active]:to-[#1273a8] data-[state=active]:text-white"
            >
              <User className="h-4 w-4 mr-2" /> Persönliche Daten
            </TabsTrigger>
            <TabsTrigger
              value="warenkorb"
              className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#63b93c] data-[state=active]:to-[#3f8f2a] data-[state=active]:text-white"
            >
              <ShoppingCart className="h-4 w-4 mr-2" /> Warenkorb
              {items.length > 0 && (
                <span className="ml-2 h-5 min-w-5 rounded-full bg-[#f7941e] text-white text-xs flex items-center justify-center px-1">
                  {items.reduce((s, i) => s + i.qty, 0)}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="bestellungen"
              className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1b9ad6] data-[state=active]:to-[#f7941e] data-[state=active]:text-white"
            >
              <Package className="h-4 w-4 mr-2" /> Bestellungen
              {orders.length > 0 && (
                <span className="ml-2 h-5 min-w-5 rounded-full bg-[#f7941e] text-white text-xs flex items-center justify-center px-1">
                  {orders.length}
                </span>
              )}
            </TabsTrigger>
            {user.is_admin && (
              <TabsTrigger
                value="produktverwaltung"
                data-testid="profile-tab-produktverwaltung"
                className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7941e] data-[state=active]:to-[#e8760a] data-[state=active]:text-white"
              >
                <ShoppingBag className="h-4 w-4 mr-2" /> Produktverwaltung
              </TabsTrigger>
            )}
          </TabsList>

          {user.is_admin && (
            <TabsContent value="produktverwaltung" className="mt-6">
              <div className="card-soft p-6 sm:p-10">
                <ProductManager />
              </div>
            </TabsContent>
          )}

          {/* Persönliche Daten */}
          <TabsContent value="daten" className="mt-6">
            <form onSubmit={save} className="card-soft p-6 sm:p-10">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-bold text-slate-600 mb-1.5 block">Name</label>
                  <input className="input-soft" value={profile.name} onChange={set("name")} placeholder="Dein Name" />
                </div>
                <div>
                  <label className="text-sm font-bold text-slate-600 mb-1.5 block flex items-center gap-1.5">
                    <Mail className="h-4 w-4 text-[#1b9ad6]" /> E-Mail
                  </label>
                  <input className="input-soft" type="email" value={profile.email} onChange={set("email")} placeholder="E-Mail-Adresse" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-bold text-slate-600 mb-1.5 block flex items-center gap-1.5">
                    <Phone className="h-4 w-4 text-[#1b9ad6]" /> Telefon
                  </label>
                  <input className="input-soft" value={profile.phone} onChange={set("phone")} placeholder="Telefon (optional)" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-bold text-slate-600 mb-1.5 block flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-[#1b9ad6]" /> Straße & Nr.
                  </label>
                  <input className="input-soft" value={profile.street} onChange={set("street")} placeholder="Straße und Hausnummer" />
                </div>
                <div>
                  <label className="text-sm font-bold text-slate-600 mb-1.5 block">PLZ</label>
                  <input className="input-soft" value={profile.zip} onChange={set("zip")} placeholder="PLZ" />
                </div>
                <div>
                  <label className="text-sm font-bold text-slate-600 mb-1.5 block">Ort</label>
                  <input className="input-soft" value={profile.city} onChange={set("city")} placeholder="Ort" />
                </div>
              </div>
              <button type="submit" className="btn-gradient mt-7">
                <Save className="h-5 w-5" /> Speichern
              </button>
            </form>
          </TabsContent>

          {/* Warenkorb */}
          <TabsContent value="warenkorb" className="mt-6">
            <div className="card-soft p-6 sm:p-10">
              {items.length === 0 ? (
                <div className="text-center py-14">
                  <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-lime-50 flex items-center justify-center">
                    <ShoppingCart className="h-8 w-8 text-[#63b93c]" />
                  </div>
                  <p className="font-display text-xl font-bold text-slate-700">Dein Warenkorb ist leer</p>
                  <p className="text-slate-500 mt-1">Stöbere durch unsere fertigen 3D-Produkte.</p>
                  <Link to="/produkte" className="btn-gradient-warm mt-5">
                    Zu den Produkten <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {items.map((item) => (
                      <div key={item.id} className="flex gap-4 rounded-2xl border border-slate-100 p-4">
                        <img src={item.image} alt={item.name} className="h-24 w-24 rounded-xl object-cover shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-slate-800">
                            {item.name} <span className="text-slate-400">– {item.variant}</span>
                          </p>
                          <p className="text-[#f7941e] font-extrabold">{formatPrice(item.price)}</p>
                          <div className="mt-2 flex items-center gap-2">
                            <button onClick={() => setQty(item.id, item.qty - 1)} className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-sky-50 hover:border-[#1b9ad6] transition-colors">
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="w-8 text-center font-bold">{item.qty}</span>
                            <button onClick={() => setQty(item.id, item.qty + 1)} className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-sky-50 hover:border-[#1b9ad6] transition-colors">
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <button onClick={() => remove(item.id)} aria-label="Entfernen" className="self-start p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                    <span className="font-semibold text-slate-600">Gesamt</span>
                    <span className="font-display text-2xl font-extrabold text-slate-900">{formatPrice(total)}</span>
                  </div>
                  <button onClick={() => setOpen(true)} className="btn-gradient-warm w-full mt-4">
                    Zur Kasse <ArrowRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          </TabsContent>

          {/* Bestellungen */}
          <TabsContent value="bestellungen" className="mt-6">
            <div className="card-soft p-6 sm:p-10">
              {ordersLoading ? (
                <div className="flex justify-center py-14">
                  <div className="h-10 w-10 rounded-full border-4 border-sky-200 border-t-[#1b9ad6] animate-spin" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-14">
                  <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-sky-50 flex items-center justify-center">
                    <Package className="h-8 w-8 text-[#1b9ad6]" />
                  </div>
                  <p className="font-display text-xl font-bold text-slate-700">Noch keine Bestellungen</p>
                  <p className="text-slate-500 mt-1">Deine bezahlten Bestellungen erscheinen hier.</p>
                  <Link to="/produkte" className="btn-gradient mt-5">
                    Jetzt stöbern <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((o) => (
                    <div key={o.session_id} className="rounded-2xl border border-slate-100 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                          <Clock className="h-4 w-4" />
                          {o.created_at
                            ? new Date(o.created_at).toLocaleDateString("de-DE", {
                                day: "2-digit", month: "long", year: "numeric",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </div>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-lime-50 border border-lime-200 px-3 py-1 text-sm font-bold text-[#3f8f2a]">
                          <CheckCircle2 className="h-4 w-4" /> Bezahlt
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {o.items.map((it, idx) => (
                          <span key={`${o.session_id}-item-${idx}`} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-1 text-sm text-slate-700">
                            {it}
                          </span>
                        ))}
                      </div>
                      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                        <div className="text-sm text-slate-500">
                          Versand: <span className="font-semibold text-slate-700">{o.shipping?.toUpperCase()}</span>
                          {o.discount_code && (
                            <span className="ml-3 text-[#3f8f2a] font-semibold">Gutschein: {o.discount_code}</span>
                          )}
                        </div>
                        <span className="font-display text-xl font-extrabold text-slate-900">
                          {formatPrice(o.amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default Profile;
